import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { authApi } from '@/api/authApi'
import { useSessionStore } from '@/stores/useSessionStore'

export const useAuthStore = defineStore('auth', () => {
  const session = useSessionStore()
  const headerMenuOpen = ref(false)

  const userInfo = computed(() => session.userInfo)
  const userName = computed(() => (session.userInfo ? session.userInfo.username : '未登录'))

  const toggleHeaderMenu = () => {
    headerMenuOpen.value = !headerMenuOpen.value
  }

  const closeHeaderMenu = () => {
    headerMenuOpen.value = false
  }

  const login = async (username, password) => {
    const data = await authApi.login(username, password)
    session.setAccessToken(data.accessToken, data.expiresIn)
    session.setUserInfo(data.user)
    return true
  }

  const smsLogin = async (phone, code) => {
    const data = await authApi.smsLogin(phone, code)
    session.setAccessToken(data.accessToken, data.expiresIn)
    session.setUserInfo(data.user)
    return true
  }

  const sendSmsCode = async (phone) => authApi.sendSmsCode(phone)

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      // ignore logout API failure, still clear local session
    } finally {
      session.clearSession()
    }
  }

  const refreshSession = async () => {
    const data = await authApi.refresh()
    session.setAccessToken(data.accessToken, data.expiresIn)
    return data
  }

  const refreshProfile = async () => {
    const me = await authApi.me()
    session.setUserInfo(me)
    return me
  }

  const refreshProfileSilently = async () => {
    if (!session.accessToken) {
      return null
    }

    try {
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.accessToken}`
        },
        credentials: 'include'
      })
      if (!response.ok) {
        return null
      }
      const me = await response.json()
      session.setUserInfo(me)
      return me
    } catch (error) {
      return null
    }
  }

  const ensureSession = async () => {
    if (session.accessToken) {
      try {
        await refreshProfile()
        return true
      } catch (error) {
        // continue to refresh path
      }
    }

    try {
      await refreshSession()
      await refreshProfile()
      return true
    } catch (error) {
      session.clearSession()
      return false
    }
  }

  return {
    userInfo,
    headerMenuOpen,
    userName,
    toggleHeaderMenu,
    closeHeaderMenu,
    login,
    sendSmsCode,
    smsLogin,
    logout,
    refreshSession,
    refreshProfile,
    refreshProfileSilently,
    ensureSession
  }
})
